import toast from "react-hot-toast";
import { memo, useMemo } from "react";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import { useSearchParams } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { Button, Dialog, DialogContent, DialogTitle, Tooltip } from "@mui/material";

import Loader from "../../../Components/Loader/Loader";
import DataTable from "../../../Components/DataGrid/DataGrid";
import ScheduledReportsTemplatesForm from "./ScheduledReportsTemplatesForm";
import { scheduledReportsApi, ScheduledReportTemplateData } from "../../api/scheduled-reports/scheduled-report.service";

function getModalProps(modalMode: string | null): { fullWidth?: boolean; maxWidth: "sm" | "md" } {
  if (modalMode === "delete") {
    return {
      maxWidth: "sm",
    };
  }

  return {
    maxWidth: "md",
    fullWidth: true,
  };
}

function getModalTitle(modalMode: string | null) {
  if (modalMode === "create") {
    return "Add New Template";
  }

  if (modalMode === "edit") {
    return "Edit Template";
  }

  return "Delete Template";
}

const ActionsColumns = (params: GridRenderCellParams) => {
  const [_, setSearchParams] = useSearchParams();

  function openEditModal() {
    setSearchParams(
      {
        modal: "edit",
        id: params.row.id,
      },
      { replace: true },
    );
  }

  function openDeleteModal() {
    setSearchParams(
      {
        modal: "delete",
        id: params.row.id,
      },
      { replace: true },
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Tooltip placement="bottom" title="Edit" disableInteractive onClick={openEditModal}>
        <EditIcon className="cursor-pointer" />
      </Tooltip>
      <Tooltip placement="bottom" title="Edit" disableInteractive onClick={openDeleteModal}>
        <DeleteIcon className="cursor-pointer" />
      </Tooltip>
    </div>
  );
};

const columns: GridColDef[] = [
  {
    field: "actions",
    headerName: "",
    type: "actions",
    renderCell: ActionsColumns,
    hideable: false,
    disableExport: true,
  },
  {
    field: "name",
    headerName: "Name",
    sortable: true,
    minWidth: 110,
    hideable: true,
    flex: 1,
  },
  {
    field: "description",
    headerName: "Description",
    sortable: true,
    minWidth: 110,
    hideable: true,
    flex: 1,
  },
  {
    field: "types",
    headerName: "Types",
    sortable: true,
    minWidth: 110,
    hideable: true,
    flex: 1,
  },
];

const { useGetTemplatesQuery, usePostTemplateMutation, useDeleteTemplateMutation, useEditTemplateMutation } =
  scheduledReportsApi;

const ScheduledReportsTemplates = () => {
  const { data = [], isFetching } = useGetTemplatesQuery();
  const [createTemplate] = usePostTemplateMutation();
  const [deleteTemplate] = useDeleteTemplateMutation();
  const [editTemplate] = useEditTemplateMutation();

  const [searchParams, setSearchParams] = useSearchParams();

  // Determine modal state from search params
  const modalMode = searchParams.get("modal");
  const id = searchParams.get("id") ?? "";

  const initial_values = useMemo(() => {
    const DEFAULT_INITIAL_VALUE = { report_types: [], name: "", description: "" };
    if (modalMode !== "edit" && !id) return DEFAULT_INITIAL_VALUE;

    const _template = data.find((template) => template.id === id);
    if (!_template) return DEFAULT_INITIAL_VALUE;

    return { report_types: _template.types, name: _template.name, description: _template.description };
  }, [modalMode, id, data]);

  // Open create modal
  const openCreateModal = () => {
    setSearchParams({ modal: "create" }, { replace: true });
  };

  const handleCloseModal = () => {
    setSearchParams({}, { replace: true }); // Removes all search params
  };

  async function handleFormSubmit(values: ScheduledReportTemplateData) {
    const toast_id = toast.loading("Submitting...");
    try {
      if (modalMode === "create") {
        await createTemplate({ body: values }).unwrap();
      } else {
        await editTemplate({ params: { id }, body: values }).unwrap();
      }
      toast.success("Success", { id: toast_id });
      handleCloseModal();
    } catch (error) {
      console.error("SCHEDULED_REPORT_TEMPLATE_ERROR", error);
      toast.error("Unable to Submit Request", { id: toast_id });
    }
  }

  async function handleDeleteItem() {
    const toast_id = toast.loading("Deleting...");
    try {
      await deleteTemplate({ params: { id } }).unwrap();
      toast.success("Success", { id: toast_id });
      handleCloseModal();
    } catch (error) {
      console.error("SCHEDULED_REPORT_TEMPLATE_DELETE_ERROR", error);
      toast.error("Unable to Submit Request", { id: toast_id });
    }
  }

  const FormComponent = memo(() => {
    if (modalMode === "edit" && isFetching)
      return (
        <div style={{ height: "10rem" }}>
          <Loader />
        </div>
      );

    if (modalMode === "delete") {
      return (
        <div>
          <div>Are you sure you want to delete this template?</div>
          <div className="flex justify-end items-center gap-2 mt-2">
            <Button variant="outlined" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button color="error" variant="contained" onClick={() => handleDeleteItem()}>
              Delete
            </Button>
          </div>
        </div>
      );
    }
    return <ScheduledReportsTemplatesForm defaultValues={initial_values} handleSubmit={handleFormSubmit} />;
  });

  const modal_props = useMemo(() => getModalProps(modalMode), [modalMode]);
  const modal_title = getModalTitle(modalMode);

  return (
    <div style={{ height: "80vh"}}>
      <Button variant="contained" onClick={openCreateModal} style={{ marginBottom: "0.5rem" }}>
        Create
      </Button>
      {(modalMode === "create" || modalMode === "edit" || modalMode === "delete") && (
        <Dialog open={true} onClose={handleCloseModal} {...modal_props}>
          <DialogTitle className="flex items-center justify-between">
            {modal_title}
            <CloseIcon className="assets-info-popup-close-icon pointer" onClick={handleCloseModal} />
          </DialogTitle>
          <DialogContent>
            <FormComponent />
          </DialogContent>
        </Dialog>
      )}
      <DataTable
        tableInfo={{
          id: "audit-log-table",
          label: "Audit Log",
          requireAuthSign: false,
          timestamp: { startTs: null, endTs: null },
        }}
        loading={isFetching}
        rows={data}
        columns={columns}
        showFilterButton={false}
      />
    </div>
  );
};

export default ScheduledReportsTemplates;
